import { useMutation, useQuery } from "@apollo/client";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Refresh from "@mui/icons-material/Refresh";
import { Box, Button, Chip, IconButton, Stack, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { useAlerts } from "components/Alerts/AlertProvider";
import { BigTooltip } from "components/BigTooltip";
import { DataGrid } from "components/DataGrid";
import { gql } from "generated-graphql";
import {
  QueueEManifestSiteAccessJobMutation,
  RcraInfoCredentialEpaIdMappingsQuery,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useMemo, useState } from "react";

import { SITE_ACCESS_REFRESH_TOOLTIP } from "../siteAccessJob";
import { EpaIdDialog } from "./EpaIdDialog";

const GET_RCRA_INFO_CREDENTIAL_EPA_ID_MAPPINGS = gql(`
  query RcraInfoCredentialEpaIdMappings {
    rcraInfoCredentialEpaIdMappings {
      credential {
        id
        name
        username
        kind
        verificationStatus
        usesEManifestApi
      }
      epaIds {
        epaId
        facilities {
          id
          name
          state
          tenant {
            id
            name
          }
        }
      }
    }
  }
`);

const QUEUE_E_MANIFEST_SITE_ACCESS_JOB = gql(`
  mutation QueueEManifestSiteAccessJob {
    queueEManifestSiteAccessJob {
      id
      status
      task
    }
  }
`);

type Row = RcraInfoCredentialEpaIdMappingsQuery["rcraInfoCredentialEpaIdMappings"][number];

export function RcraInfoCredentials() {
  const alerts = useAlerts();
  useBreadcrumb({ label: "RCRAInfo Credentials" });

  const [viewingCredential, setViewingCredential] = useState<Row | null>(null);
  const { data, loading } = useQuery(GET_RCRA_INFO_CREDENTIAL_EPA_ID_MAPPINGS);

  const [queueJob, { loading: isQueueing }] = useMutation<QueueEManifestSiteAccessJobMutation>(
    QUEUE_E_MANIFEST_SITE_ACCESS_JOB,
    {
      onCompleted: (data) => {
        const jobId = data.queueEManifestSiteAccessJob.id;
        alerts.success(
          <>
            Site access job queued successfully.{" "}
            <a
              href={`/staff/jobs/${jobId}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
              onClick={(e) => {
                e.preventDefault();
                window.open(`/staff/jobs/${jobId}`, "_blank", "noopener,noreferrer");
              }}
            >
              View job {jobId}
            </a>
          </>
        );
      },
      onError: (error) => {
        alerts.error("Failed to queue site access job", error);
      },
    }
  );

  const columns: GridColDef<Row>[] = useMemo(
    () => [
      {
        field: "credentialName",
        headerName: "Credential",
        flex: 1.5,
        renderCell: (params) => (
          <Stack spacing={0.5}>
            <Typography variant="body2" fontWeight="medium">
              {params.row.credential.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.credential.username}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "verificationStatus",
        headerName: "Status",
        width: 150,
        renderCell: (params) => (
          <Chip
            label={params.row.credential.verificationStatus}
            size="small"
            color={params.row.credential.verificationStatus === "GOOD" ? "success" : "default"}
          />
        ),
      },
      {
        field: "usesEManifestApi",
        headerName: "e-Manifest API",
        width: 150,
        renderCell: (params) =>
          params.row.credential.usesEManifestApi ? (
            <Chip label="API" size="small" color="success" />
          ) : (
            <Chip label="Web only" size="small" color="default" />
          ),
      },
      {
        field: "epaIdsCount",
        headerName: "EPA IDs",
        width: 120,

        renderCell: (params) => (
          <Button
            variant="text"
            size="small"
            onClick={() => setViewingCredential(params.row)}
            sx={{ textTransform: "none" }}
          >
            {params.row.epaIds.length}
          </Button>
        ),
      },
      {
        field: "facilitiesCount",
        headerName: "Facilities",
        width: 120,
        renderCell: ({ row }) => {
          const allFacilities = row.epaIds.flatMap((m) => m.facilities);
          const uniqueFacilities = Array.from(
            new Map(allFacilities.map((f) => [f.id, f])).values()
          );
          return <Typography variant="body2">{uniqueFacilities.length}</Typography>;
        },
      },
    ],
    []
  );

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2} sx={{ mb: 2 }} alignItems="end">
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => queueJob()}
            disabled={isQueueing}
          >
            {isQueueing ? "Queueing..." : "Refresh EPA ID Mappings"}
          </Button>
          <BigTooltip title={SITE_ACCESS_REFRESH_TOOLTIP}>
            <IconButton size="small">
              <InfoOutlined color="disabled" />
            </IconButton>
          </BigTooltip>
        </Stack>
      </Stack>
      <DataGrid
        rows={data?.rcraInfoCredentialEpaIdMappings ?? []}
        columns={columns}
        loading={loading}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnSorting
        getRowId={(row) => row.credential.id}
        getRowHeight={() => "auto"}
      />
      {viewingCredential && (
        <EpaIdDialog credential={viewingCredential} onClose={() => setViewingCredential(null)} />
      )}
    </Box>
  );
}
